/**
 * The last bulk edit the user sent to the confirmation, kept in this browser so the next bulk
 * edit starts from it. A cancelled edit is never kept: only "Check the changes" writes it.
 *
 * Each entity has its own entry, because each offers different fields. On the custom list
 * search only the "Custom Lists" tab is remembered. Its schedule tabs always start as
 * "Keep as is", because a schedule edit is for the schedules picked that time.
 *
 * Storage can be missing or full (a private window, blocked site data), and an entry can be from
 * an older version of the dialog. Either way the dialog starts empty, as it did before it
 * remembered anything. That is an expected outcome, not an error, so it is not reported.
 */

const STORAGE_PREFIX = 'bulk-edit-memory:';

/** One field row of the bulk edit, without the key the dialog draws it by. */
export interface RememberedBulkEditRow {
    field: string;
    op: string | null;
    value: string | null;
    label: string | null;
}

function read(key: string): unknown {
    try {
        const stored = window.localStorage.getItem(STORAGE_PREFIX + key);
        return stored === null ? null : JSON.parse(stored);
    } catch {
        return null;
    }
}

function write(key: string, value: unknown): void {
    try {
        window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    } catch {
        // Nothing is remembered, and the next edit starts empty.
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

export function readRememberedBulkEditRows(key: string): RememberedBulkEditRow[] {
    const stored = read(key);
    if (!Array.isArray(stored)) return [];
    return stored
        .filter((row): row is Record<string, unknown> => isRecord(row) && typeof row.field === 'string')
        .map((row) => ({ field: row.field as string, op: stringOrNull(row.op), value: stringOrNull(row.value), label: stringOrNull(row.label) }));
}

export function rememberBulkEditRows(key: string, rows: RememberedBulkEditRow[]): void {
    write(key, rows);
}
